import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Instagram,
    Youtube,
    ExternalLink,
    AlertTriangle,
    Linkedin,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SITE_META } from "@/data/staticContent";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    async function submitNewsletter(e) {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter", { email });
            if (data.already_subscribed) {
                toast.info("Ya estabas suscrito. No hace falta repetirse.");
            } else {
                toast.success(
                    "Suscripción confirmada. Bienvenido al vacío editorial.",
                );
            }
            setEmail("");
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "No pudimos suscribirte. Intenta de nuevo.",
            );
        } finally {
            setLoading(false);
        }
    }

    return (
        <footer
            className="bg-[#0a0a0a] border-t-2 border-[var(--danger)] mt-24"
            data-testid="site-footer"
        >
            <div className="container-x py-16 md:py-20">
                {/* Newsletter */}
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mb-16 pb-16 border-b border-[var(--line)]">
                    <div>
                        <p className="eyebrow mb-3">Boletín · 00/</p>
                        <h3 className="font-display text-3xl md:text-4xl mb-4 text-[var(--paper-2)]">
                            Suscríbete a las cicatrices.
                        </h3>
                        <p className="font-mono text-sm text-[var(--ink-muted)] max-w-md">
                            No habrá ofertas. No habrá descuentos. Solo
                            antipoesía, fragmentos del libro y entradas
                            nuevas. Cancela cuando quieras.
                        </p>
                    </div>
                    <form
                        onSubmit={submitNewsletter}
                        className="flex flex-col gap-4 self-end"
                        data-testid="footer-newsletter-form"
                    >
                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)]">
                            Email · campo obligatorio para entrar al expediente
                        </label>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu.email@dominio.cl"
                                className="input-dossier flex-1"
                                data-testid="footer-newsletter-email"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-brutal btn-brutal-danger disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="footer-newsletter-submit"
                            >
                                {loading ? "Procesando…" : "Suscribirse"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Links grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
                    <div>
                        <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)] mb-4">
                            01 · Libros de adicciones
                        </h4>
                        <ul className="space-y-2 font-mono text-sm">
                            <li>
                                <Link
                                    to="/libro"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-libro"
                                >
                                    El Principio del Mapa Inverso
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/descargar"
                                    className="text-[var(--warning)] hover:text-[var(--danger)] font-bold"
                                    data-testid="footer-link-comprar"
                                >
                                    Descargar libro gratis
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/libros-de-adicciones"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-libros-adicciones"
                                >
                                    Libros de adicciones
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/libro-de-sobriedad"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-libro-sobriedad"
                                >
                                    Libro de sobriedad
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/cuentos-para-adictos"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-cuentos"
                                >
                                    Cuentos para adictos
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/mapa-inverso"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-mapa-inverso"
                                >
                                    App Mapa Inverso
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)] mb-4">
                            02 · Ecosistema
                        </h4>
                        <ul className="space-y-2 font-mono text-sm">
                            <li>
                                <Link
                                    to="/autodestruccion-sa"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-autodestruccion"
                                >
                                    Autodestrucción S.A.
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/glosario"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-glosario"
                                >
                                    Glosario del Adicto
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/antipoesia"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-antipoesia"
                                >
                                    Antipoesía Terapéutica
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/curriculum"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-curriculum"
                                >
                                    Currículum del Adicto
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/blog"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-blog"
                                >
                                    Archivo de Cicatrices
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)] mb-4">
                            03 · Autor
                        </h4>
                        <ul className="space-y-2 font-mono text-sm">
                            <li>
                                <Link
                                    to="/nelson-gonzalez-rodriguez"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-sobre"
                                >
                                    Nelson González Rodríguez
                                </Link>
                            </li>
                            <li>
                                <a
                                    href="https://www.linkedin.com/in/nelsongonzalezr/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)] inline-flex items-center gap-2"
                                    data-testid="footer-link-linkedin"
                                >
                                    <Linkedin className="w-3.5 h-3.5" />
                                    LinkedIn
                                </a>
                            </li>
                            <li>
                                <Link
                                    to="/contacto"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)]"
                                    data-testid="footer-link-contacto"
                                >
                                    Contacto
                                </Link>
                                <span
                                    className="block font-hand text-[var(--warning)] text-lg -rotate-3 mt-1 ml-3"
                                    data-testid="footer-no-droga-note"
                                >
                                    pero no me ofrezcas droga ↗
                                </span>
                            </li>
                            <li>
                                <Link
                                    to="/salida"
                                    className="text-[var(--danger)] hover:text-[var(--paper-2)] font-bold"
                                    data-testid="footer-link-salida"
                                >
                                    ⚠ Salida de emergencia
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)] mb-4">
                            04 · Redes & plataformas
                        </h4>
                        <ul className="space-y-2 font-mono text-sm">
                            <li>
                                <a
                                    href={SITE_META.socials.sinAdicciones}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--warning)] hover:text-[var(--paper-2)] inline-flex items-center gap-2"
                                    data-testid="footer-link-sinadicciones"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    SinAdicciones.org
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://plansobrio.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--warning)] hover:text-[var(--paper-2)] inline-flex items-center gap-2"
                                    data-testid="footer-link-plansobrio"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    PlanSobrio.com
                                </a>
                            </li>
                            <li>
                                <a
                                    href={SITE_META.socials.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)] inline-flex items-center gap-2"
                                    data-testid="footer-link-instagram"
                                >
                                    <Instagram className="w-3.5 h-3.5" />
                                    Instagram
                                </a>
                            </li>
                            <li>
                                <a
                                    href={SITE_META.socials.tiktok}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)] inline-flex items-center gap-2"
                                    data-testid="footer-link-tiktok"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    TikTok
                                </a>
                            </li>
                            <li>
                                <a
                                    href={SITE_META.socials.youtube}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--paper-2)] hover:text-[var(--danger)] inline-flex items-center gap-2"
                                    data-testid="footer-link-youtube"
                                >
                                    <Youtube className="w-3.5 h-3.5" />
                                    YouTube
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Disclaimer */}
                <div className="border border-[var(--danger)] p-6 md:p-8 mb-10">
                    <div className="flex items-start gap-4">
                        <AlertTriangle className="w-6 h-6 text-[var(--danger)] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)] mb-3">
                                Advertencia responsable · Léeme antes de cerrar la pestaña
                            </p>
                            <p className="font-mono text-sm leading-relaxed text-[var(--paper)]">
                                Este sitio contiene textos personales, irónicos y
                                testimoniales sobre adicción, consumo, recaídas
                                y recuperación. No reemplaza evaluación médica,
                                terapia psicológica, psiquiatría, tratamiento
                                profesional ni ayuda de emergencia. Si estás en
                                riesgo, no puedes parar o tienes ideas de
                                hacerte daño, busca ayuda profesional inmediata
                                o acude a un servicio de urgencia.{" "}
                                <Link
                                    to="/salida"
                                    className="text-[var(--warning)] underline"
                                >
                                    Salida de emergencia
                                </Link>
                                .
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="flex flex-col md:flex-row justify-between gap-4 pt-6 border-t border-[var(--line)] font-mono text-[11px] text-[var(--ink-dim)] uppercase tracking-[0.2em]">
                    <p>
                        © {new Date().getFullYear()} El Mapa Inverso ·{" "}
                        {SITE_META.authorName} · Hecho con cicatrices.
                    </p>
                    <p>
                        <a
                            href={SITE_META.socials.original}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[var(--danger)]"
                        >
                            Origen: nitanadicto.com
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
}
